import { request } from '../../api';

export interface IdentificationTotals {
  readonly visits: number;
  readonly counted: number;
  readonly identified: number;
  readonly unidentified: number;
  readonly linked: number;
  readonly verifiedPhone: number;
  readonly consented: number;
  readonly declined: number;
  readonly pending: number;
  readonly serviceExcluded: number;
  readonly identifiedShare: number;
  readonly unidentifiedShare: number;
}

export interface ReasonRow {
  readonly code: string;
  readonly label: string;
  readonly visits: number;
  readonly shareOfDeclined: number;
}

export interface ShiftRow {
  readonly shiftId: string;
  readonly openedAt: string;
  readonly closedAt: string | null;
  readonly visits: number;
  readonly identified: number;
  readonly identifiedShare: number;
}

export interface OperatorRow {
  readonly operatorId: string | null;
  readonly displayName: string | null;
  readonly visits: number;
  readonly identified: number;
  readonly identifiedShare: number;
}

export interface HistoryRow {
  readonly day: string;
  readonly visits: number;
  readonly identified: number;
  readonly identifiedShare: number;
}

export interface IdentificationDashboard {
  readonly totals: IdentificationTotals;
  readonly byReason: readonly ReasonRow[];
  readonly byShift: readonly ShiftRow[];
  readonly byOperator: readonly OperatorRow[];
  readonly history: readonly HistoryRow[];
  readonly policy: {
    readonly rule: string;
    readonly excludeServiceVehicles: boolean;
    readonly excludeReasonCodes: readonly string[];
    readonly minLprConfidence: number;
    readonly requireIdentifiedAt: boolean;
  };
  readonly generatedAt: string;
}

export interface ExecutiveRow {
  /** null — абонентская плата без объекта. */
  readonly siteId: string | null;
  readonly siteName: string;
  readonly visits: number;
  readonly identified: number;
  readonly identifiedShare: number;
  readonly orders: number;
  readonly grossRevenueMinor: number;
  readonly refundedMinor: number;
  readonly subscriptionRevenueMinor: number;
  readonly revenueMinor: number;
  readonly averageCheckMinor: number;
}

export interface ExecutiveReport {
  readonly rows: readonly ExecutiveRow[];
  readonly totals: Omit<ExecutiveRow, 'siteId' | 'siteName'>;
}

export interface CohortReport {
  readonly rows: readonly {
    readonly cohortMonth: string;
    readonly size: number;
    readonly visits: number;
    readonly repeatVisits: number;
    readonly repeatCustomers: number;
    readonly repeatShare: number;
    readonly visitsPerCustomer: number;
    readonly cells: readonly {
      readonly monthsSince: number;
      readonly customers: number;
      readonly visits: number;
      readonly retention: number;
    }[];
  }[];
}

export interface SubscriptionReport {
  readonly activated: number;
  readonly cancelled: number;
  readonly activeAtStart: number;
  readonly activeAtEnd: number;
  readonly churn: number;
  readonly uses: number;
  readonly bySite: readonly {
    readonly siteId: string | null;
    readonly siteName: string;
    readonly activated: number;
    readonly cancelled: number;
    readonly activeAtStart: number;
    readonly activeAtEnd: number;
    readonly churn: number;
    readonly uses: number;
    readonly subscriptions: number;
  }[];
}

export interface MetricRow {
  readonly id: string;
  readonly value: number;
  readonly numerator: number;
  readonly denominator: number;
  readonly definition: {
    readonly titleRu: string;
    readonly titleKk: string;
    readonly unit: string;
    readonly formula: string;
    readonly source: string;
    readonly scope: string;
  };
}

export interface MetricsResponse {
  readonly from: string;
  readonly to: string;
  readonly metrics: readonly MetricRow[];
}

export interface Policy {
  readonly siteId: string | null;
  readonly rule: string;
  readonly minLprConfidence: number;
  readonly requireIdentifiedAt: boolean;
  readonly excludeServiceVehicles: boolean;
  readonly excludeReasonCodes: readonly string[];
  readonly activeWindowDays: number;
  readonly feedbackSlaHours: number;
  readonly maxPeriodDays: number;
  readonly statementTimeoutMs: number;
  readonly rowLimit: number;
}

export interface PolicyResponse {
  readonly policy: Policy;
}

export interface DatasetInfo {
  readonly id: string;
  readonly titleRu: string;
  readonly titleKk: string;
  readonly containsPii: boolean;
  readonly networkOnly: boolean;
  readonly kind: 'mart' | 'rows';
  readonly columns: readonly string[];
}

export interface ExportResult {
  readonly exportId: string;
  readonly dataset: string;
  readonly format: string;
  readonly containsPii: boolean;
  readonly rowCount: number;
  readonly truncated: boolean;
  readonly content: string;
}

export interface ExportLogEntry {
  readonly id: string;
  readonly scope: string;
  readonly rowCount: number;
  readonly containsPii: boolean;
  readonly createdAt: string;
  readonly filters: Record<string, unknown>;
}

export interface MartStatus {
  readonly view: string;
  readonly populated: boolean;
  readonly rows: number;
}

function period(from: string, to: string, siteId?: string | null): string {
  const params = new URLSearchParams({ from, to });
  if (siteId) params.set('site_id', siteId);
  return params.toString();
}

export function fetchIdentification(
  from: string,
  to: string,
  siteId?: string | null,
): Promise<IdentificationDashboard> {
  return request('GET', `/api/v1/reports/identification?${period(from, to, siteId)}`);
}

export function fetchExecutive(
  from: string,
  to: string,
  siteId?: string | null,
): Promise<ExecutiveReport> {
  return request('GET', `/api/v1/reports/executive?${period(from, to, siteId)}`);
}

export function fetchCohorts(from: string, to: string): Promise<CohortReport> {
  return request('GET', `/api/v1/reports/cohorts?${period(from, to)}`);
}

export function fetchSubscriptions(from: string, to: string): Promise<SubscriptionReport> {
  return request('GET', `/api/v1/reports/subscriptions?${period(from, to)}`);
}

export function fetchMetrics(
  from: string,
  to: string,
  siteId?: string | null,
): Promise<MetricsResponse> {
  return request('GET', `/api/v1/reports/metrics?${period(from, to, siteId)}`);
}

export function fetchPolicy(siteId?: string | null): Promise<PolicyResponse> {
  const params = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  return request('GET', `/api/v1/reports/policy${params}`);
}

export function savePolicy(patch: Partial<Policy> & { siteId: string | null }): Promise<Policy> {
  return request('PATCH', '/api/v1/reports/policy', patch);
}

export function fetchDatasets(): Promise<readonly DatasetInfo[]> {
  return request('GET', '/api/v1/reports/datasets');
}

export function runExport(body: {
  dataset: string;
  format: 'csv' | 'json';
  from?: string;
  to?: string;
  siteId?: string;
}): Promise<ExportResult> {
  return request('POST', '/api/v1/reports/export', body);
}

export function fetchExportLog(containsPii?: boolean): Promise<readonly ExportLogEntry[]> {
  const params = containsPii === undefined ? '' : `?contains_pii=${containsPii}`;
  return request('GET', `/api/v1/reports/exports${params}`);
}

export function fetchMarts(): Promise<readonly MartStatus[]> {
  return request('GET', '/api/v1/reports/marts');
}

export function refreshMarts(): Promise<{ refreshed: readonly unknown[] }> {
  return request('POST', '/api/v1/reports/refresh');
}

export interface ForecastDay {
  readonly day: string;
  readonly weekday: string;
  readonly weather: {
    readonly tempMax: number | null;
    readonly tempMin: number | null;
    readonly precipitationMm: number;
    readonly snowfallCm: number;
    readonly weatherCode: number | null;
  } | null;
  readonly expected: number;
  readonly low: number;
  readonly high: number;
  readonly weekdayMean: number;
}

export interface ForecastReport {
  readonly siteId: string;
  readonly model: {
    readonly trainingDays: number;
    readonly holdoutDays: number;
    readonly mape: number | null;
    readonly baselineMape: number | null;
    readonly residualSd: number;
  };
  readonly days: readonly ForecastDay[];
  readonly recent: readonly { readonly day: string; readonly actual: number; readonly predicted: number }[];
}

export interface SiteLocation {
  readonly siteId: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly timezone: string;
}

export function fetchForecast(siteId: string): Promise<ForecastReport> {
  return request('GET', `/api/v1/reports/forecast?site_id=${encodeURIComponent(siteId)}`);
}

export function fetchLocations(siteId: string): Promise<readonly SiteLocation[]> {
  return request('GET', `/api/v1/reports/forecast/location?site_id=${encodeURIComponent(siteId)}`);
}

export function saveLocation(body: { siteId: string; latitude: number; longitude: number }): Promise<SiteLocation> {
  return request('PUT', '/api/v1/reports/forecast/location', body);
}

export function syncWeather(siteId: string): Promise<unknown> {
  return request('POST', `/api/v1/reports/forecast/weather/sync?site_id=${encodeURIComponent(siteId)}`);
}
